// Inbox/LinkToCustomer — for a conversation with no lead or customer: pick an existing
// customer (search) or create a lead from the contact details, then link the thread
// (api/inboxThreads.linkThread — lane C's leg). Compact Modal on desktop, Sheet on phone.
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { searchCustomers } from "../../../api/customersApi";
import { createCrmLead } from "../../../api/leadsApi";
import { linkThread } from "../../../api/inboxThreads";
import Modal from "../../ui/Modal";
import Sheet from "../../ui/Sheet";
import Button from "../../ui/button";
import { Input } from "../../ui/input";
import EmptyState from "../../ui/EmptyState";

function useIsPhone() {
  const get = () => typeof window !== "undefined" && !!window.matchMedia?.("(max-width: 640px)").matches;
  const [on, setOn] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia?.("(max-width: 640px)");
    if (!mq) return undefined;
    const fn = () => setOn(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", fn) : mq.addListener(fn);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", fn) : mq.removeListener(fn));
  }, []);
  return on;
}

export default function LinkToCustomer({ row, mode = "link", onClose, onLinked }) {
  const isPhone = useIsPhone();
  const [q, setQ] = useState(row?.contact_phone || row?.contact_name || "");
  const [rows, setRows] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const who = row?.display_name || row?.contact_name || row?.contact_phone || row?.contact_email || "this contact";

  useEffect(() => {
    if (mode !== "link" || q.trim().length < 2) { setRows([]); return undefined; }
    let live = true;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const res = await searchCustomers(q.trim());
        const d = res?.data ?? res;
        const list = Array.isArray(d) ? d : d?.results || [];
        if (live) setRows(list.slice(0, 30));
      } catch {
        if (live) setRows([]);
      } finally {
        if (live) setSearching(false);
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [q, mode]);

  const link = async (c) => {
    const id = c.customer_id || c.id;
    setBusy(true);
    try {
      const linked = await linkThread(row, { customer_id: id });
      toast.success(`Linked to ${c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || "the customer"}`);
      onLinked?.(linked);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Couldn’t link this conversation");
    } finally {
      setBusy(false);
    }
  };

  const createLead = async () => {
    setBusy(true);
    try {
      const res = await createCrmLead({
        name: row?.contact_name || row?.display_name || row?.contact_phone || "Inbox contact",
        phone_number: row?.contact_phone || undefined,
        email: row?.contact_email || undefined,
        source: "inbox",
      });
      const lead = res?.data ?? res;
      const leadId = lead?.id;
      if (!leadId) throw new Error("The lead was created but came back without an id.");
      const linked = await linkThread(row, { lead_id: leadId });
      toast.success(`Lead created for ${who}`);
      onLinked?.(linked);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Couldn’t create the lead");
    } finally {
      setBusy(false);
    }
  };

  const body = mode === "create" ? (
    <div className="ltc">
      <p className="ltc__p">Create a lead from this conversation’s contact details and link the thread to it.</p>
      <dl className="ltc__facts">
        <dt>Name</dt><dd>{row?.contact_name || row?.display_name || "—"}</dd>
        <dt>Phone</dt><dd>{row?.contact_phone || "—"}</dd>
        <dt>Email</dt><dd>{row?.contact_email || "—"}</dd>
      </dl>
      <div className="ltc__actions">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={createLead} loading={busy}>Create lead</Button>
      </div>
    </div>
  ) : (
    <div className="ltc">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customers by name, phone or email" aria-label="Search customers" autoFocus />
      {q.trim().length < 2 ? (
        <p className="ltc__p">Type a name, phone or email to find the customer this conversation belongs to.</p>
      ) : searching && !rows.length ? (
        <p className="ltc__p">Searching…</p>
      ) : !rows.length ? (
        <EmptyState title="No customer matches" body="Try the phone number, or create a lead instead." />
      ) : (
        <div className="ltc__list">
          {rows.map((c) => {
            const name = c.full_name || [c.first_name, c.last_name].filter(Boolean).join(" ") || c.company_name || "Unnamed";
            const number = c.primary_phone || c.phone || c.phone_number || "";
            return (
              <button type="button" className="ltc__row" key={c.customer_id || c.id || number} onClick={() => link(c)} disabled={busy}>
                <span className="ltc__name">{name}</span>
                <span className="ltc__num">{number || c.email || ""}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const title = mode === "create" ? "Create lead" : "Link to customer";
  if (isPhone) return <Sheet open onClose={onClose} title={title}>{body}</Sheet>;
  return <Modal open onClose={onClose} title={title} size="compact">{body}</Modal>;
}
